// LAP-646 — Card "Évolution temporelle" : courbe 3 mois + highlights par bucket
import React from 'react'
import { Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Line } from 'react-chartjs-2'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import moment from 'moment'
import CardHeader from '../../Investments/_shared/CardHeader'
import EmptyHelp from './EmptyHelp'
import Delta from '../../Investments/_shared/Delta'

const BUCKET_PALETTE = {
  famille: '#34d399',
  amis: '#63a0ff',
  pro: '#f59e0b',
  amour: '#f472b6',
  autres: '#94a3b8',
}

const BUCKET_LABELS = {
  famille: 'Famille',
  amis: 'Amis',
  pro: 'Pro',
  amour: 'Amour',
  autres: 'Autres',
}

export default function EvolutionCard({ data, loading }) {
  const { t } = useTranslation('layout')
  const months = data?.months || []
  const highlights = (data?.highlights || []).slice(0, 4)

  // Vide si aucun mois retourné OU si tous les mois sont à 0 interactions.
  // La spec FS0012 §2.2.5 demande une courbe non-vide pour parler d'évolution.
  const hasAnyInteraction = months.some((m) => (m.total || 0) > 0)
  if (!loading && (months.length === 0 || !hasAnyInteraction)) {
    return (
      <Box display="flex" flexDirection="column" height="100%">
        <CardHeader
          icon={<TimelineOutlinedIcon fontSize="small" />}
          title={t('social.evolution.title', 'Évolution temporelle')}
          subtitle={t('social.evolution.subtitle', 'Interactions sur 3 mois')}
        />
        <EmptyHelp
          icon={<TimelineOutlinedIcon sx={{ fontSize: 22 }} />}
          title={t('social.empty.evolution.title', 'Pas assez d’historique pour tracer une courbe')}
          description={t(
            'social.empty.evolution.description',
            'L’évolution couvre les 3 derniers mois et compare aux 3 mêmes mois de l’an passé. Crée régulièrement des événements avec des participants dans ton calendrier pour voir la tendance se construire.',
          )}
          actions={[
            { key: 'calendar', label: t('social.empty.cta.openCalendar', 'Ouvrir le calendrier') },
            { key: 'contacts', label: t('social.empty.cta.addContact', 'Ajouter un contact') },
          ]}
        />
      </Box>
    )
  }

  // Datasets : un par bucket présent au moins une fois
  const allBuckets = new Set()
  months.forEach((m) => Object.keys(m.by_bucket || {}).forEach((k) => allBuckets.add(k)))
  const buckets = Array.from(allBuckets)
  const labels = months.map((m) => moment(m.label, 'YYYY-MM').format('MMM YY'))

  const chartData = {
    labels,
    datasets: [
      // Total en arrière-plan (gras, blanc)
      {
        label: t('social.evolution.total', 'Total'),
        data: months.map((m) => m.total || 0),
        borderColor: 'rgba(255,255,255,0.85)',
        backgroundColor: 'rgba(255,255,255,0.85)',
        tension: 0.35,
        borderWidth: 2.5,
        pointRadius: 4,
        pointBackgroundColor: '#fff',
      },
      ...buckets.map((b) => ({
        label: BUCKET_LABELS[b] || b,
        data: months.map((m) => (m.by_bucket || {})[b] || 0),
        borderColor: BUCKET_PALETTE[b] || '#94a3b8',
        backgroundColor: BUCKET_PALETTE[b] || '#94a3b8',
        tension: 0.35,
        borderWidth: 1.5,
        pointRadius: 2,
      })),
    ],
  }
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: { display: false },
      legend: {
        display: true,
        position: 'bottom',
        labels: { color: 'rgba(255,255,255,0.7)', font: { size: 10 }, boxWidth: 8, usePointStyle: true, padding: 8 },
      },
    },
    scales: {
      x: { ticks: { color: 'rgba(255,255,255,0.55)' }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { ticks: { color: 'rgba(255,255,255,0.55)' }, grid: { color: 'rgba(255,255,255,0.06)' } },
    },
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <CardHeader
        icon={<TimelineOutlinedIcon fontSize="small" />}
        title={t('social.evolution.title', 'Évolution temporelle')}
        subtitle={t('social.evolution.subtitle', 'Interactions sur 3 mois')}
      />
      <Box sx={{ flexGrow: 1, minHeight: 180, mt: 2 }}>
        <Line data={chartData} options={options} />
      </Box>
      {highlights.length > 0 && (
        <Box mt={1.5} sx={{ borderTop: '1px dashed rgba(255,255,255,0.08)', pt: 1.25 }}>
          <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
            {t('social.evolution.highlights', 'Progressions par cercle')}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 0.75 }}>
            {highlights.map((h, i) => (
              <Box
                key={i}
                display="flex"
                alignItems="center"
                sx={{
                  p: 0.75,
                  borderRadius: 1.25,
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  fontSize: 12,
                  mr: 1,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: BUCKET_PALETTE[h.bucket] || '#94a3b8',
                    mr: 0.75,
                  }}
                />
                <Box sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, mr: 1 }}>
                  {BUCKET_LABELS[h.bucket] || h.bucket}
                </Box>
                {h.delta_pct !== null && h.delta_pct !== undefined ? (
                  <Delta value={h.delta_pct} unit="%" />
                ) : (
                  <Box sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    {h.prev} → {h.curr}
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}
